/**
 * The scorecard — headline bar plus the four-tab body.
 *
 * Port of `buildScorecard`/`buildHeadlineBar`/`buildTabs`/`buildOverview`/`buildScoring`/
 * `buildDistribution` from the legacy atlas.js. Markup is preserved exactly (the golden
 * parity harness compares rendered HTML), with one class of change: three
 * model-influenced fields that were interpolated raw now go through
 * `sanitizeRichText`. See src/core/format.ts for why that is an allowlist and not a
 * blanket escape.
 */
import type { Asset } from '../../core/types.ts';
import {
  PRINCIPLE_LABELS,
  esc,
  expandTierLabel,
  sanitizeRichText,
  statusClass,
} from '../../core/format.ts';
import { backendAvailable } from '../backend.ts';
import { buildShareCardPanel, wireShareCardPanel } from './share-card.ts';

interface WhyBullet {
  label?: string;
  detail?: string;
}
interface PrincipleRationale {
  bullets?: WhyBullet[];
  outcome?: string;
}
interface TargetBlock {
  label?: string;
  venues?: string[];
}
interface DistributionRationale {
  sleeve_fit?: string[];
  primary_targets?: TargetBlock;
  secondary_targets?: TargetBlock;
  operational?: string;
}

/** Callback invoked when a pending asset's "Run Live Enrichment" button is pressed. */
export type EnrichHandler = (asset: Asset, button: HTMLButtonElement) => void;

export function buildScorecard(asset: Asset, onEnrich?: EnrichHandler): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'scorecard';
  wrap.appendChild(buildHeadlineBar(asset));
  wrap.appendChild(buildTabs(asset, onEnrich));
  return wrap;
}

export function buildHeadlineBar(asset: Asset): HTMLElement {
  const bar = document.createElement('div');
  bar.className = 'headline-bar';
  const sCls = statusClass(asset.status);
  const overrideTag = asset.override
    ? '<span class="sleeve-tag override-tag">· Committee Override</span>'
    : '';
  const shariaTag = asset.sharia ? '<span class="sleeve-tag sharia">· Sharia-eligible</span>' : '';
  const tierSec = asset.tier_secondary
    ? ` · ${expandTierLabel(asset.tier_secondary)} secondary`
    : '';
  const commentary = asset.headline
    ? `<p class="commentary">${esc(asset.headline)}</p>`
    : '<p class="commentary pending">Headline commentary pending publication. Score and structural assignments are issued; narrative analysis follows in the next coverage cycle or via live dynamic enrichment.</p>';

  bar.innerHTML =
    `<div class="headline-score ${sCls}">` +
    `<div class="num">${asset.score}</div>` +
    '<div class="scale">/ 100</div>' +
    '</div>' +
    '<div class="headline-meta">' +
    `<div class="asset-eyebrow">${esc(asset.exchange_full || asset.exchange)} · ${esc(asset.code)} · ${esc(asset.isin || asset.tickers[0])}</div>` +
    `<h2 class="asset-name">${esc(asset.name)} <span class="asset-class">(${esc(asset.asset_class)})</span></h2>` +
    '<div class="status-row">' +
    `<span class="status-pill ${sCls}"><span class="dot"></span>${esc(asset.status)}</span>` +
    `<span class="sleeve-tag"><strong>${esc(asset.sleeve)}</strong> sleeve · ${expandTierLabel(asset.tier_primary)} primary${tierSec}${overrideTag}${shariaTag}</span>` +
    '</div>' +
    commentary +
    '</div>';
  return bar;
}

export function buildTabs(asset: Asset, onEnrich?: EnrichHandler): HTMLElement {
  const wrap = document.createElement('div');
  wrap.innerHTML =
    '<div class="tabs-bar">' +
    '<button class="tab-button active" data-tab="overview">Asset Fact Sheet</button>' +
    '<button class="tab-button" data-tab="scoring">Scoring</button>' +
    '<button class="tab-button" data-tab="distribution">Distribution</button>' +
    '<button class="tab-button" data-tab="sharecard">Share Card</button>' +
    '</div>' +
    `<div class="tab-content" data-content="overview">${buildOverview(asset)}</div>` +
    `<div class="tab-content hidden" data-content="scoring">${buildScoring(asset)}</div>` +
    `<div class="tab-content hidden" data-content="distribution">${buildDistribution(asset)}</div>` +
    `<div class="tab-content hidden" data-content="sharecard">${buildShareCardPanel(asset)}</div>`;

  const tabButtons = [...wrap.querySelectorAll<HTMLButtonElement>('.tab-button')];
  const panels = [...wrap.querySelectorAll<HTMLElement>('.tab-content')];
  for (const btn of tabButtons) {
    btn.addEventListener('click', () => {
      const tab = btn.dataset['tab'];
      for (const b of tabButtons) b.classList.toggle('active', b === btn);
      for (const p of panels) p.classList.toggle('hidden', p.dataset['content'] !== tab);
    });
  }

  wireShareCardPanel(wrap, asset);

  for (const toggle of wrap.querySelectorAll<HTMLButtonElement>('.sc-row .why-toggle')) {
    toggle.addEventListener('click', () => toggle.closest('.sc-row')?.classList.toggle('open'));
  }
  if (onEnrich) {
    for (const btn of wrap.querySelectorAll<HTMLButtonElement>('[data-enrich-btn]')) {
      btn.addEventListener('click', () => onEnrich(asset, btn));
    }
  }
  return wrap;
}

export function buildOverview(asset: Asset): string {
  if (asset.content !== 'complete' || !asset.fact_sheet) {
    return buildPendingNotice(
      'Asset fact sheet pending publication.',
      'Live enrichment via the Atlas backend will populate market metrics, reference links, recent earnings, and the issuer description.',
    );
  }
  const f = asset.fact_sheet as Record<string, unknown>;
  const kpis: Array<[string, unknown, unknown]> = [
    ['Market Cap', f['market_cap'], f['market_cap_unit']],
    ['5-Year USD Return', f['return_5y'], f['return_5y_unit']],
    ['90-Day ADV', f['adv'], f['adv_unit']],
    ['Free Float', f['free_float'], f['free_float_unit']],
    ['Dividend Yield', f['dividend_yield'], f['dividend_yield_unit']],
    ['Currency', f['currency'], ''],
  ];

  let html = '<div class="fs-kpis">';
  for (const [label, value, unit] of kpis) {
    html +=
      `<div class="fs-kpi"><div class="label">${esc(label)}</div>` +
      `<div class="value">${esc(value)}${unit ? `<span class="unit">${esc(unit)}</span>` : ''}</div></div>`;
  }
  html += '</div>';

  const about = f['about'] as string[] | undefined;
  if (about?.length) {
    html += '<div class="fs-section fs-about"><h3>About the Issuer</h3>';
    for (const p of about) html += `<p>${esc(p)}</p>`;
    html += '</div>';
  }

  const references = f['references'] as Array<{ url?: string; label?: string }> | undefined;
  if (references?.length) {
    html += '<div class="fs-section"><h3>Reference Links</h3><div class="fs-references">';
    for (const r of references) {
      html += `<a href="${esc(safeUrl(r.url))}" target="_blank" rel="noopener noreferrer">${esc(r.label)}</a>`;
    }
    html += '</div></div>';
  }

  const earnings = f['earnings'] as
    | { periods?: string[]; rows?: Array<{ metric?: string; values?: string[] }>; currency?: string }
    | undefined;
  if (earnings) {
    html +=
      '<div class="fs-section fs-earnings"><h3>Recent Financial Performance · Three-Quarter Trailing</h3>';
    html += '<table><thead><tr><th>Metric</th>';
    for (const p of earnings.periods ?? []) html += `<th class="num">${esc(p)}</th>`;
    html += '</tr></thead><tbody>';
    for (const row of earnings.rows ?? []) {
      html += `<tr><td>${esc(row.metric)}</td>`;
      for (const v of row.values ?? []) html += `<td class="num">${esc(v)}</td>`;
      html += '</tr>';
    }
    html += '</tbody></table>';
    if (earnings.currency) html += `<div class="currency-note">${esc(earnings.currency)}</div>`;
    html += '</div>';
  }

  html += `<div class="fs-asof">Figures as of ${esc(f['as_of'])}. Subject to revision under the standard publication cycle. Fact-sheet metrics drawn from issuer disclosures, exchange filings, and authoritative data providers.</div>`;
  return html;
}

export function buildScoring(asset: Asset): string {
  const rationale = asset.principle_rationale as PrincipleRationale[] | undefined;
  const pending = asset.content !== 'complete' || !rationale;

  let html = '<div class="sc-list">';
  PRINCIPLE_LABELS.forEach((label, i) => {
    const s = asset.principle_scores[i] ?? 0;
    const pct = (s / 5) * 100;
    html +=
      '<div class="sc-row"><div class="sc-row-head">' +
      `<span class="num">0${i + 1}</span>` +
      `<div class="name-bar"><span class="pname">${esc(label)}</span>` +
      `<div class="bar-track"><div class="bar-fill" style="width:${pct}%"></div></div></div>` +
      `<span class="pscore">${s.toFixed(2)}</span>` +
      '</div>';
    if (!pending) {
      html +=
        '<button class="why-toggle" type="button"><span class="caret">▸</span> Why this score</button>' +
        `<div class="why-content">${buildWhyContent(rationale?.[i])}</div>`;
    }
    html += '</div>';
  });
  html += '</div>';

  if (pending) {
    html += buildPendingNotice(
      'Per-principle analytical commentary pending publication.',
      'Score values are issued; benchmark-supported rationale follows in the next coverage cycle or via live dynamic enrichment.',
    );
  }
  return html;
}

export function buildWhyContent(why: PrincipleRationale | undefined): string {
  if (!why) return '';
  let h = '';
  for (const b of why.bullets ?? []) {
    // `detail` is model-influenced on off-cache assets — sanitized, not raw.
    h +=
      `<div class="why-bullet"><div class="label">${esc(b.label)}</div>` +
      `<div class="detail">${sanitizeRichText(b.detail)}</div></div>`;
  }
  if (why.outcome) {
    h += `<div class="why-outcome"><span class="label">Outcome</span>${esc(why.outcome)}</div>`;
  }
  return h;
}

export function buildDistribution(asset: Asset): string {
  const secondary = asset.tier_secondary
    ? `<div class="dist-block green"><div class="label">Tier Routing · Secondary</div><div class="value">${expandTierLabel(asset.tier_secondary)}</div></div>`
    : '<div class="dist-block"><div class="label">Tier Routing · Secondary</div><div class="value" style="color:var(--text-muted)">None</div><div class="value-sub">Tier exclusion or sleeve constraint</div></div>';

  const overviewHTML =
    '<div class="dist-grid">' +
    `<div class="dist-block"><div class="label">Sleeve Assignment</div><div class="value">${esc(asset.sleeve)}</div></div>` +
    `<div class="dist-block amber"><div class="label">Tier Routing · Primary</div><div class="value">${expandTierLabel(asset.tier_primary)}</div></div>` +
    secondary +
    '</div>';

  const d = asset.distribution_rationale as unknown as DistributionRationale | undefined;
  if (asset.content !== 'complete' || !d) {
    return (
      overviewHTML +
      buildPendingNotice(
        'Distribution rationale pending publication.',
        'Sleeve and tier assignments are issued; analytical narrative with named distribution targets follows in the next coverage cycle or via live dynamic enrichment.',
      )
    );
  }

  let html = overviewHTML;
  const overrideRationale = (asset as unknown as { override_rationale?: string }).override_rationale;
  if (asset.override && overrideRationale) {
    html += `<div class="dist-override"><div class="label">Committee Override</div><p>${esc(overrideRationale)}</p></div>`;
  }
  if (d.sleeve_fit?.length) {
    html += '<div class="dist-section"><h3>Sleeve Fit Rationale</h3><ul class="dist-bullets">';
    // Model-influenced on off-cache assets — sanitized, not raw.
    for (const b of d.sleeve_fit) html += `<li>${sanitizeRichText(b)}</li>`;
    html += '</ul></div>';
  }

  html += '<div class="dist-section"><h3>Distribution Targets</h3><div class="dist-targets">';
  if (d.primary_targets) {
    html +=
      `<div class="dist-target-block"><div class="target-label">Primary</div><div class="target-tier">${esc(d.primary_targets.label)}</div><ul class="dist-venues">`;
    for (const v of d.primary_targets.venues ?? []) html += `<li>${sanitizeRichText(v)}</li>`;
    html += '</ul></div>';
  }
  if (d.secondary_targets) {
    html +=
      `<div class="dist-target-block"><div class="target-label">Secondary</div><div class="target-tier">${esc(d.secondary_targets.label)}</div><ul class="dist-venues">`;
    for (const v of d.secondary_targets.venues ?? []) html += `<li>${sanitizeRichText(v)}</li>`;
    html += '</ul></div>';
  } else {
    html +=
      '<div class="dist-target-block no-tier"><div class="target-label">Secondary</div><div class="target-tier">No secondary tier</div><div style="font-size:13px;color:var(--text-muted);line-height:1.55;">Secondary distribution restricted by sleeve assignment or tier-gate exclusion.</div></div>';
  }
  html += '</div></div>';

  if (d.operational) {
    html += `<div class="dist-section"><h3>Operational Sequencing</h3><div class="dist-operational">${esc(d.operational)}</div></div>`;
  }
  return html;
}

export function buildPendingNotice(primary: string, sub: string): string {
  const available = backendAvailable();
  const buttonLabel = available
    ? 'Run Live Enrichment'
    : 'Live Enrichment Unavailable (No Backend Configured)';
  const disabled = available ? '' : 'disabled';
  return (
    '<div class="pending-notice">' +
    '<div class="icon">— PENDING COVERAGE —</div>' +
    `<div class="text">${esc(primary)}</div>` +
    `<div class="sub">${esc(sub)}</div>` +
    `<div class="action"><button type="button" data-enrich-btn ${disabled}>${buttonLabel}</button></div>` +
    '</div>'
  );
}

/**
 * Reference links come from model output on off-cache assets. Escaping alone would not
 * stop `javascript:` in an href, so anything that is not http(s) is dropped.
 */
function safeUrl(url: string | undefined): string {
  if (!url) return '';
  try {
    const parsed = new URL(url, 'https://tblocks.io');
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : '';
  } catch {
    return '';
  }
}
